// Catálogo único de variáveis de mensagem (espelha o renderer do servidor em
// apps/server/src/common/message-template.util.ts). Sintaxe canônica: {{key}}.

export type VariableGroup =
  | 'lead'
  | 'agendamento'
  | 'responsavel_org'
  | 'saudacao'
  | 'campo'

export interface MessageVariable {
  key: string
  label: string
  group: VariableGroup
  sample: string
}

export const VARIABLE_GROUP_LABEL: Record<VariableGroup, string> = {
  lead: 'Lead',
  agendamento: 'Agendamento',
  responsavel_org: 'Responsável / Organização',
  saudacao: 'Saudação e data',
  campo: 'Campos personalizados',
}

export const MESSAGE_VARIABLES: MessageVariable[] = [
  { key: 'nome', label: 'Nome', group: 'lead', sample: 'João Silva' },
  {
    key: 'primeiro_nome',
    label: 'Primeiro nome',
    group: 'lead',
    sample: 'João',
  },
  { key: 'empresa', label: 'Empresa', group: 'lead', sample: 'Acme Ltda' },
  {
    key: 'telefone',
    label: 'Telefone',
    group: 'lead',
    sample: '(11) 99999-0000',
  },
  { key: 'email', label: 'E-mail', group: 'lead', sample: 'joao@acme.com' },
  {
    key: 'agendamento_titulo',
    label: 'Título',
    group: 'agendamento',
    sample: 'Reunião comercial',
  },
  {
    key: 'agendamento_data',
    label: 'Data e hora',
    group: 'agendamento',
    sample: '14/06/2026, 15:00',
  },
  {
    key: 'agendamento_hora',
    label: 'Hora',
    group: 'agendamento',
    sample: '15:00',
  },
  {
    key: 'agendamento_link',
    label: 'Link',
    group: 'agendamento',
    sample: 'https://meet.exemplo/abc',
  },
  {
    key: 'responsavel',
    label: 'Responsável',
    group: 'responsavel_org',
    sample: 'Ana Souza',
  },
  {
    key: 'responsavel_primeiro_nome',
    label: 'Primeiro nome do responsável',
    group: 'responsavel_org',
    sample: 'Ana',
  },
  {
    key: 'organizacao',
    label: 'Organização',
    group: 'responsavel_org',
    sample: 'Minha Empresa',
  },
  { key: 'saudacao', label: 'Saudação', group: 'saudacao', sample: 'Bom dia' },
  {
    key: 'hora_atual',
    label: 'Hora atual',
    group: 'saudacao',
    sample: '14:32',
  },
  {
    key: 'data_atual',
    label: 'Data atual',
    group: 'saudacao',
    sample: '13/06/2026',
  },
]

const SAMPLE_BY_KEY: Record<string, string> = Object.fromEntries(
  MESSAGE_VARIABLES.map((variable) => [variable.key, variable.sample])
)

const ALIASES: Record<string, string> = {
  lead_name: 'nome',
  greeting: 'saudacao',
  titulo: 'agendamento_titulo',
  data: 'agendamento_data',
  hora: 'agendamento_hora',
  link: 'agendamento_link',
}

/** Variáveis disponíveis nos grupos informados, na ordem do catálogo. */
export function variablesForGroups(groups: VariableGroup[]): MessageVariable[] {
  return MESSAGE_VARIABLES.filter((variable) => groups.includes(variable.group))
}

/** Converte os campos personalizados da org em variáveis do grupo "campo". */
export function customFieldsToVariables(
  fields: Array<{ key: string; name: string }>
): MessageVariable[] {
  return fields.map((field) => ({
    key: field.key,
    label: field.name,
    group: 'campo' as const,
    sample: field.name,
  }))
}

function resolveSample(
  raw: string,
  extraSamples: Record<string, string>
): string | null {
  const lower = raw.toLowerCase()
  const key = ALIASES[lower] ?? lower
  const sample = extraSamples[key] ?? extraSamples[raw] ?? SAMPLE_BY_KEY[key]
  return sample ?? null
}

/**
 * Avalia a condição de um bloco {{#se ...}} usando os valores de exemplo.
 * Espelha `evalCondition` do servidor (igualdade `=`/`!=` e teste de preenchido).
 */
function evalConditionPreview(
  expr: string,
  extraSamples: Record<string, string>
): boolean {
  const cmp = expr.match(/^([\w.]+)\s*(=|!=)\s*"([^"]*)"$/)
  if (cmp) {
    const rawKey = cmp[1] ?? ''
    const op = cmp[2] ?? '='
    const target = cmp[3] ?? ''
    const value = (resolveSample(rawKey, extraSamples) ?? '')
      .trim()
      .toLowerCase()
    const isEqual = value === target.trim().toLowerCase()
    return op === '=' ? isEqual : !isEqual
  }
  const truthy = expr.match(/^([\w.]+)$/)
  if (truthy) {
    return (resolveSample(truthy[1] ?? '', extraSamples) ?? '').trim() !== ''
  }
  return false
}

/**
 * Substitui as variáveis por valores de exemplo, para o preview. Espelha o
 * renderer do servidor: resolve condicionais {{#se ...}}...{{senao}}...{{/se}},
 * depois {{key}} (com valor padrão `{{key | "fallback"}}`) e a sintaxe legada
 * {nome|titulo|data|hora|link}. `extraSamples` cobre campos personalizados.
 */
export function renderMessagePreview(
  text: string,
  extraSamples: Record<string, string> = {}
): string {
  const withConditionals = text.replace(
    /\{\{#se\s+(.+?)\}\}([\s\S]*?)(?:\{\{senao\}\}([\s\S]*?))?\{\{\/se\}\}/g,
    (_match, expr: string, ifText: string, elseText: string | undefined) =>
      evalConditionPreview(expr, extraSamples) ? ifText : (elseText ?? '')
  )

  return withConditionals
    .replace(
      /\{\{\s*([\w.]+)\s*(?:\|\s*"([^"]*)")?\s*\}\}/g,
      (match, raw: string, fallback: string | undefined) => {
        const sample = resolveSample(raw, extraSamples)
        if (sample === null) return match
        return sample === '' && fallback !== undefined ? fallback : sample
      }
    )
    .replace(/\{(nome|titulo|data|hora|link)\}/g, (match, raw: string) => {
      const sample = resolveSample(raw, extraSamples)
      return sample === null ? match : sample
    })
}
